import { type RowComponentProps } from 'react-window';

import { setRefAtIndex } from '../../../../../utilities/common/utils';
import { useArrayRef } from '../../../../../utilities/common/hooks/useRefArray';

export type TooltipData = {
  x: number;
  y: number;
  label: string;
  score: number;
  uniqueRespondents: number;
};

export type RowProps = {
  barsSorted: Array<{
    label: string;
    score: number;
    uniqueRespondents: number;
    color: string | undefined;
  }>;
  showRespondentsLine: boolean;
  barRefs: ReturnType<typeof useArrayRef<HTMLButtonElement>>;
  setTooltip: React.Dispatch<React.SetStateAction<TooltipData | null>>;
  hoverTimeoutRef: React.MutableRefObject<number | null>;
};

const Row = ({
  index,
  style,
  barsSorted,
  showRespondentsLine,
  barRefs,
  setTooltip,
  hoverTimeoutRef,
}: RowComponentProps<RowProps>) => {
  const { label, score, color, uniqueRespondents } = barsSorted[index];
  const widthPercent = (score / 100) * 100;

  const showTooltip = () => {
    const rect = barRefs.current[index]?.getBoundingClientRect();
    if (rect) {
      setTooltip({
        x: rect.left + rect.width / 2,
        y: rect.bottom + 12,
        label,
        score,
        uniqueRespondents,
      });
    }
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  };

  const hideTooltip = () => {
    // Adding timeout to avoid immediate hide on mouse leave
    hoverTimeoutRef.current = window.setTimeout(() => {
      setTooltip(null);
    }, 100);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      showTooltip();
    }
  };

  return (
    <div
      className="intl-widget-ignore-element flex items-center gap-x-4"
      style={style}
    >
      <span className="text-black-700 w-[131px] truncate text-right text-xs">
        {label}
      </span>

      <div className="bg-taupe-400 relative flex h-5 w-full">
        <button
          ref={setRefAtIndex(barRefs, index)}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          onFocus={showTooltip}
          onBlur={hideTooltip}
          onKeyDown={onKeyDown}
          className="group relative h-5 rounded-r-md"
          style={{ width: `${widthPercent}%`, backgroundColor: color }}
        >
          <div className="flex h-full items-center justify-end pr-1 text-xs font-semibold text-white">
            {score}
          </div>
        </button>
      </div>

      <div className="text-black-700 w-[180px] truncate text-xs">
        {showRespondentsLine && uniqueRespondents}
      </div>
    </div>
  );
};

export default Row;
